import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { User, Mail, Phone, Crown, KeyRound, Save, Camera } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Password Change Component
const PasswordChangeSection = ({ user }) => {
  const [showPasswordSection, setShowPasswordSection] = useState(false);
  const [email, setEmail] = useState('');
  const [emailSent, setEmailSent] = useState(false);
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSendPasswordResetEmail = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error('Por favor ingresa tu email');
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      toast.success('✅ Email enviado. Revisa tu bandeja de entrada');
      setEmailSent(true);
    } catch (error) {
      toast.error('Error al enviar email');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (!resetCode || !newPassword || !confirmPassword) {
      toast.error('Por favor completa todos los campos');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error('Las contraseñas no coinciden');
      return;
    }
    
    if (newPassword.length < 8) {
      toast.error('La contraseña debe tener al menos 8 caracteres');
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        email,
        reset_code: resetCode,
        new_password: newPassword
      });
      
      toast.success('✅ Contraseña cambiada correctamente');
      setShowPasswordSection(false);
      setEmailSent(false);
      setResetCode('');
      setNewPassword('');
      setConfirmPassword('');
      setEmail('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cambiar contraseña');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-yellow-900/30 border-l-4 border-yellow-400 p-4 rounded-lg">
        <div className="flex">
          <KeyRound className="h-5 w-5 text-yellow-400 mr-3 flex-shrink-0" />
          <div>
            <h3 className="text-sm font-medium text-yellow-300">
              Cambiar Contraseña
            </h3>
            <p className="mt-2 text-sm text-yellow-200">
              Por seguridad, te enviaremos un código de verificación a tu email para confirmar el cambio de contraseña.
            </p>
          </div>
        </div>
      </div>

      {!showPasswordSection ? (
        <button
          onClick={() => setShowPasswordSection(true)}
          className="w-full bg-gradient-to-r from-yellow-500 to-amber-500 text-black py-3 rounded-lg font-medium hover:opacity-90 transition-all flex items-center justify-center gap-2"
        >
          <KeyRound size={20} />
          Cambiar Contraseña
        </button>
      ) : (
        <div className="space-y-6">
          {!emailSent ? (
            <form onSubmit={handleSendPasswordResetEmail} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Confirma tu Email
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-3 bg-gray-800 text-white rounded-lg border border-gray-700 focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                  placeholder={user?.email}
                  required
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-yellow-500 to-amber-500 text-black py-3 rounded-lg font-medium hover:opacity-90 transition-all disabled:opacity-50"
              >
                {loading ? 'Enviando...' : 'Enviar Código de Verificación'}
              </button>
            </form>
          ) : (
            <form onSubmit={handleResetPassword} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Código de Verificación (revisa tu email)
                </label>
                <input
                  type="text"
                  value={resetCode}
                  onChange={(e) => setResetCode(e.target.value)}
                  className="w-full px-4 py-3 bg-gray-800 text-white rounded-lg border border-gray-700 focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none text-center text-2xl tracking-widest font-bold"
                  placeholder="000000"
                  maxLength={6}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Nueva Contraseña
                </label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full px-4 py-3 bg-gray-800 text-white rounded-lg border border-gray-700 focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                  placeholder="Mínimo 8 caracteres"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Confirmar Nueva Contraseña
                </label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full px-4 py-3 bg-gray-800 text-white rounded-lg border border-gray-700 focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                  placeholder="Repite tu contraseña"
                  required
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-yellow-500 to-amber-500 text-black py-3 rounded-lg font-medium hover:opacity-90 transition-all disabled:opacity-50"
              >
                {loading ? 'Cambiando...' : 'Confirmar Cambio de Contraseña'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowPasswordSection(false);
                  setEmailSent(false);
                }}
                className="w-full text-gray-400 hover:text-gray-300 py-2"
              >
                Cancelar
              </button>
            </form>
          )}
        </div>
      )}
    </div>
  );
};


export const Profile = () => {
  const { user, checkAuth } = useAuth();
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({ name: '', phone: '', picture: '', is_avatar_public: true });
  const [promoCode, setPromoCode] = useState('');
  const [promoLoading, setPromoLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  
  const handlePictureUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/upload/avatar`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setFormData(prev => ({ ...prev, picture: res.data.url }));
      await checkAuth();
      toast.success('Foto actualizada');
    } catch (error) {
      toast.error('Error al subir la foto');
    }
  };

  useEffect(() => {
    if (user) {
      setFormData({ 
        name: user.name || '', 
        phone: user.phone || '', 
        picture: user.picture || '',
        is_avatar_public: user.is_avatar_public !== undefined ? user.is_avatar_public : true
      });
    }
  }, [user]);

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      const { picture, ...rest } = formData;
      await axios.put(`${API}/profile`, rest);
      await checkAuth();
      setEditing(false);
      toast.success('Perfil actualizado correctamente');
    } catch (error) {
      toast.error('Error al actualizar el perfil');
    } finally {
      setSaving(false);
    }
  };

  const handlePromoCode = async () => {
    if (!promoCode.trim()) return;
    setPromoLoading(true);
    try {
      const response = await axios.post(`${API}/profile/promo-code`, { code: promoCode });
      toast.success(response.data.message);
      await checkAuth();
      setPromoCode('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Codigo invalido');
    } finally {
      setPromoLoading(false);
    }
  };

  const handleUpgrade = async (packageId) => {
    try {
      const originUrl = window.location.origin;
      const response = await axios.post(
        `${API}/payments/checkout?origin_url=${encodeURIComponent(originUrl)}&package_id=${packageId}`,
        {}
      );
      window.location.href = response.data.url;
    } catch (error) {
      toast.error('Error al procesar el pago');
    }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-gray-950 p-4 md:p-8 lg:p-12">
      <div className="max-w-5xl mx-auto pt-12">
        <h1 
          className={`text-4xl md:text-5xl font-bold mb-8 bg-gradient-to-r from-yellow-500 to-amber-500 bg-clip-text text-transparent`}
          data-testid="profile-title"
        >
          Mi Perfil
        </h1>
        
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Profile Card */}
          <div className="lg:col-span-1">
            <div className="bg-gray-900 border border-white/10 rounded-2xl p-6">
              <div className="flex flex-col items-center text-center">
                <div className="relative mb-4">
                  {(formData.picture || user.picture) ? (
                    <img src={formData.picture || user.picture} alt={user.name} className="w-24 h-24 rounded-full object-cover ring-4 ring-white/20" />
                  ) : (
                    <div className={`w-24 h-24 rounded-full bg-gradient-to-br from-yellow-500 to-amber-500 flex items-center justify-center text-white text-3xl font-bold`}>
                      {user.name?.[0]?.toUpperCase() || 'U'}
                    </div>
                  )}
                  {editing && (
                    <label className="absolute bottom-0 right-0 bg-yellow-500 p-2 rounded-full cursor-pointer hover:bg-yellow-600 transition">
                      <Camera size={16} className="text-black" />
                      <input type="file" accept="image/*" onChange={handlePictureUpload} className="hidden" />
                    </label>
                  )}
                </div>
                <h2 className="text-xl font-bold text-white mb-1">{user.name}</h2>
                <p className="text-sm text-gray-400 mb-3">{user.email}</p>
                <span className={`text-xs font-bold px-3 py-1.5 rounded-full ${
                  ['premium', 'profesional'].includes(user.subscription_plan)
                    ? 'bg-yellow-500/20 text-yellow-400 ring-1 ring-yellow-500/30'
                    : ['essential', 'usuario_avanzado'].includes(user.subscription_plan)
                    ? 'bg-purple-500/20 text-purple-400 ring-1 ring-purple-500/30'
                    : 'bg-white/10 text-gray-400'
                }`}>
                  {['premium', 'profesional'].includes(user.subscription_plan) ? 'Profesional'
                    : ['essential', 'usuario_avanzado'].includes(user.subscription_plan) ? 'Avanzado'
                    : 'Plan Gratuito'}
                </span>
              </div>
            </div>
          </div>

          {/* Settings */}
          <div className="lg:col-span-2 space-y-6">
            {/* Personal Info */}
            <div className="bg-gray-900 border border-white/10 rounded-2xl p-6" data-testid="personal-info-section">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                  <User size={18} className="text-gray-400" />
                  Informacion Personal
                </h3>
                {!editing ? (
                  <button 
                    onClick={() => setEditing(true)} 
                    className="text-sm text-gray-400 hover:text-white transition-colors"
                    data-testid="edit-profile-btn"
                  >
                    Editar
                  </button>
                ) : (
                  <button 
                    onClick={handleSaveProfile}
                    disabled={saving}
                    className={`flex items-center gap-2 text-sm font-medium px-4 py-2 rounded-lg bg-gradient-to-r from-yellow-500 to-amber-500 text-black transition-all hover:opacity-90 disabled:opacity-50`}
                    data-testid="save-profile-btn"
                  >
                    <Save size={14} />
                    {saving ? 'Guardando...' : 'Guardar'}
                  </button>
                )}
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1.5 block">Nombre</label>
                  {editing ? (
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      className="w-full bg-gray-800 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-purple-500/50"
                      data-testid="profile-name-input"
                    />
                  ) : (
                    <div className="text-white text-base">{user.name}</div>
                  )}
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1.5 block">Email</label>
                  <div className="text-white text-base flex items-center gap-2">
                    <Mail size={14} className="text-gray-500" />
                    {user.email}
                  </div>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1.5 block">Telefono</label>
                  {editing ? (
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      placeholder="+34 600 000 000"
                      className="w-full bg-gray-800 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-purple-500/50"
                      data-testid="profile-phone-input"
                    />
                  ) : (
                    <div className="text-white text-base flex items-center gap-2">
                      <Phone size={14} className="text-gray-500" />
                      {user.phone || 'No configurado'}
                    </div>
                  )}
                </div>
                
                {/* Avatar Privacy Toggle */}
                <div className="pt-4 border-t border-white/5">
                  <div className="flex items-center justify-between">
                    <div>
                      <label className="text-sm font-medium text-white mb-1 block">Avatar Público</label>
                      <p className="text-xs text-gray-400">Permite que otros usuarios vean tu foto de perfil en chats y mensajes</p>
                    </div>
                    <button
                      onClick={() => {
                        if (editing) {
                          setFormData({...formData, is_avatar_public: !formData.is_avatar_public});
                        } else {
                          // Si no está editando, guardamos directamente
                          const newValue = !formData.is_avatar_public;
                          setFormData({...formData, is_avatar_public: newValue});
                          axios.put(`${API}/profile`, { is_avatar_public: newValue })
                            .then(() => {
                              checkAuth();
                              toast.success(newValue ? 'Avatar ahora es público' : 'Avatar ahora es privado');
                            })
                            .catch(() => toast.error('Error al actualizar privacidad'));
                        }
                      }}
                      className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${
                        formData.is_avatar_public ? `bg-gradient-to-r from-yellow-500 to-amber-500` : 'bg-gray-700'
                      }`}
                    >
                      <span
                        className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-lg transition-transform ${
                          formData.is_avatar_public ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Sección de Cambio de Contraseña */}
            <div className="bg-gray-900 border border-white/10 rounded-2xl p-6" data-testid="password-section">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2 mb-6">
                <KeyRound size={18} className="text-yellow-400" />
                Seguridad
              </h3>
              <PasswordChangeSection user={user} />
            </div>

            {/* Subscription */}
            <div className="bg-gray-900 border border-white/10 rounded-2xl p-6" data-testid="subscription-section">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2 mb-6">
                <Crown size={18} className="text-yellow-400" />
                Suscripcion
              </h3>
              
              {['premium', 'profesional'].includes(user.subscription_plan) ? (
                <div className="bg-gradient-to-r from-yellow-500/10 to-orange-500/10 border border-yellow-500/20 rounded-xl p-5">
                  <div className="flex items-center gap-3 mb-2">
                    <Crown size={24} className="text-yellow-400" />
                    <span className="text-xl font-bold text-yellow-400">Plan Profesional Activo</span>
                  </div>
                  <p className="text-gray-400 text-sm">Tienes acceso completo a todas las funciones: CRM, organizaciones, análisis ROI, IA y más.</p>
                </div>
              ) : ['usuario_avanzado', 'essential'].includes(user.subscription_plan) ? (
                <div className="space-y-4">
                  <div className="bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/20 rounded-xl p-5 mb-2">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-xl font-bold text-purple-400">Plan Usuario Avanzado Activo</span>
                    </div>
                    <p className="text-gray-400 text-sm">Propiedades ilimitadas, datos IA, Knowledge e Invest activos.</p>
                  </div>
                  <div className="border border-yellow-500/30 bg-yellow-500/5 rounded-xl p-5">
                    <div className="text-sm font-medium text-yellow-400 mb-1">Actualiza a</div>
                    <div className="text-2xl font-bold text-white mb-1">Profesional</div>
                    <div className="text-3xl font-bold text-white mb-3">€24.99<span className="text-sm text-gray-400">/mes</span></div>
                    <a
                      href={`${process.env.REACT_APP_STRIPE_LINK_PROFESIONAL || 'https://buy.stripe.com/00w6oI8Vv7aB0fBgqUbMQ00'}?prefilled_email=${encodeURIComponent(user.email)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block w-full py-2.5 rounded-lg bg-gradient-to-r from-yellow-500 to-orange-500 text-black text-sm font-bold hover:opacity-90 transition-all text-center"
                    >
                      Actualizar a Profesional
                    </a>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="border border-purple-500/30 rounded-xl p-5 hover:border-purple-400/50 transition-all">
                      <div className="text-sm font-medium text-purple-400 mb-1">Popular</div>
                      <div className="text-2xl font-bold text-white mb-1">Usuario Avanzado</div>
                      <div className="text-3xl font-bold text-white mb-3">€4.99<span className="text-sm text-gray-400">/mes</span></div>
                      <a
                        href={`${process.env.REACT_APP_STRIPE_LINK_AVANZADO || 'https://buy.stripe.com/dRm5kEgnX7aBaUfdeIbMQ01'}?prefilled_email=${encodeURIComponent(user.email)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block w-full py-2.5 rounded-lg bg-gradient-to-r from-yellow-500 to-amber-500 text-black text-sm font-bold hover:opacity-90 transition-all text-center"
                        data-testid="upgrade-basic-btn"
                      >
                        Suscribirme
                      </a>
                    </div>
                    <div className="border border-yellow-500/30 bg-yellow-500/5 rounded-xl p-5 hover:border-yellow-400/50 transition-all">
                      <div className="text-sm font-medium text-yellow-400 mb-1">Completo</div>
                      <div className="text-2xl font-bold text-white mb-1">Profesional</div>
                      <div className="text-3xl font-bold text-white mb-3">€24.99<span className="text-sm text-gray-400">/mes</span></div>
                      <a
                        href={`${process.env.REACT_APP_STRIPE_LINK_PROFESIONAL || 'https://buy.stripe.com/00w6oI8Vv7aB0fBgqUbMQ00'}?prefilled_email=${encodeURIComponent(user.email)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block w-full py-2.5 rounded-lg bg-gradient-to-r from-yellow-500 to-orange-500 text-black text-sm font-bold hover:opacity-90 transition-all text-center"
                        data-testid="upgrade-premium-btn"
                      >
                        Suscribirme
                      </a>
                    </div>
                  </div>

                  {/* Promo Code */}
                  <div className="border border-white/10 rounded-xl p-5" data-testid="promo-code-section">
                    <h4 className="text-sm font-semibold text-white flex items-center gap-2 mb-3">
                      <KeyRound size={14} className="text-gray-400" />
                      Codigo Promocional
                    </h4>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={promoCode}
                        onChange={(e) => setPromoCode(e.target.value)}
                        placeholder="Introduce tu codigo"
                        className="flex-1 bg-gray-800 border border-white/10 rounded-lg px-4 py-2.5 text-white text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/50"
                        data-testid="promo-code-input"
                      />
                      <button
                        onClick={handlePromoCode}
                        disabled={promoLoading || !promoCode.trim()}
                        className={`px-5 py-2.5 rounded-lg bg-gradient-to-r from-yellow-500 to-amber-500 text-black text-sm font-medium hover:opacity-90 disabled:opacity-50 transition-all`}
                        data-testid="apply-promo-btn"
                      >
                        {promoLoading ? '...' : 'Aplicar'}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
